import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import useAssistant from '../../store/useAssistant';
import { assistantAPI, sendMessage } from '../../services/assistant';
import { savedReportsAPI } from '../../services/api';
import ReportDisplay from '../widgets/ReportDisplay';
import ReportEditor from '../report-editor/ReportEditor';
import AssistantText from './AssistantText';
import AiMark from './AiMark';
import Sparkline from '../widgets/Sparkline';
import { formatKpiValue, describeChange, STATUS_LABEL, CADENCE_LABEL } from '../../utils/kpiFormat';
import '../../styles/Assistant.css';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

/** What the user is looking at, sent when a conversation starts. */
function pageContext(location, dashboardId) {
  const report = /^\/reports\/(\d+)/.exec(location.pathname);
  if (report) return { reportId: Number(report[1]) };
  if (location.pathname === '/' && dashboardId) return { dashboardId: Number(dashboardId) };
  return {};
}

/** A draft KPI: its value this period against the target, and its trend. */
function KpiCard({ item }) {
  const { kpi, evaluation: e } = item;
  const change = describeChange({ ...e, kpi });
  const trend = (e.trend || []).map((p) => p.value);
  return (
    <figure className="ai-card ai-result ai-result--draft ai-kpi">
      <figcaption className="ai-result-title">
        <span className="ai-badge">{item.basedOn ? 'Draft change' : 'Draft KPI'}</span>
        <span>{item.title}</span>
      </figcaption>
      {item.basedOn && (
        <div className="ai-changes">
          <p>Changes to &quot;{item.basedOn.name}&quot;</p>
          {item.changes?.length ? <ul>{item.changes.map((c) => <li key={c}>{c}</li>)}</ul> : <p className="ai-muted">No changes yet.</p>}
        </div>
      )}
      <div className="ai-kpi-body">
        <div>
          <p className="ai-kpi-period">{e.period}</p>
          <p className="ai-kpi-value">{formatKpiValue(e.value, kpi.format, kpi.unit)}</p>
          {change && <p className={`ai-kpi-change ${change.improving === null ? '' : change.improving ? 'ai-kpi-change--good' : 'ai-kpi-change--bad'}`}>{change.label}</p>}
        </div>
        <div className="ai-kpi-side">
          <span className={`ai-kpi-status ai-kpi-status--${e.status}`}>{STATUS_LABEL[e.status] || STATUS_LABEL.none}</span>
          {e.target !== null && e.target !== undefined && <span className="ai-muted">Target {formatKpiValue(e.target, kpi.format, kpi.unit)} ({kpi.direction === 'lower' ? 'lower' : 'higher'} is better)</span>}
          <Sparkline values={trend} width={140} height={34} title={`${item.title} trend`} />
        </div>
      </div>
      <p className="ai-working"><span>How this is worked out</span>{item.measureTitle}, {(CADENCE_LABEL[kpi.cadence] || kpi.cadence).toLowerCase()}{kpi.filters?.length ? `, where ${kpi.filters.map((f) => `${f.member.split('.').pop().replace(/_/g, ' ')} ${f.operator} ${(f.values || []).join(', ')}`).join('; ')}` : ''}{kpi.department ? ` · ${kpi.department}` : ''}</p>
      <div className="ai-card-actions">
        <span className="ai-muted">Not saved. Ask for changes, or ask to save it.</span>
      </div>
    </figure>
  );
}

/** The person's choice of model and effort, remembered per company in this browser. */
const choiceKey = (companyId) => `assistantChoice:${companyId}`;
function readChoice(companyId) {
  try { return JSON.parse(localStorage.getItem(choiceKey(companyId)) || 'null') || {}; } catch { return {}; }
}
function saveChoice(companyId, choice) {
  try { localStorage.setItem(choiceKey(companyId), JSON.stringify(choice)); } catch { /* not remembered */ }
}

/** Model and effort, from those the company allows; shown as text when there is nothing to choose. */
function ModelChoice({ settings, value, onChange, disabled }) {
  if (!settings?.models?.length) return null;
  const model = settings.models.find((m) => m.id === value.model) || settings.models[0];
  const effort = settings.efforts.find((e) => e.id === value.reasoningEffort) || settings.efforts[0];
  return (
    <div className="ai-model-choice">
      {settings.models.length > 1 ? (
        <select aria-label="Model" title={model.description} value={model.id} disabled={disabled} onChange={(e) => onChange({ ...value, model: e.target.value })}>
          {settings.models.map((m) => <option key={m.id} value={m.id} title={m.description}>{m.label}</option>)}
        </select>
      ) : <span title={model.description}>{model.label}</span>}
      {effort && (settings.efforts.length > 1 ? (
        <select aria-label="Effort" title={effort.description} value={effort.id} disabled={disabled} onChange={(e) => onChange({ ...value, reasoningEffort: e.target.value })}>
          {settings.efforts.map((x) => <option key={x.id} value={x.id} title={x.description}>{x.label} effort</option>)}
        </select>
      ) : <span title={effort.description}>{effort.label} effort</span>)}
    </div>
  );
}

function ResultCard({ item, onOpenInEditor }) {
  const table = item.table;
  return (
    <figure className={`ai-card ai-result ${item.draft ? 'ai-result--draft' : ''}`}>
      <figcaption className="ai-result-title">
        {item.draft && <span className="ai-badge">{item.basedOn ? 'Draft change' : 'Draft'}</span>}
        <span>{item.title}</span>
      </figcaption>
      {item.basedOn && (
        <div className="ai-changes">
          <p>Changes to &quot;{item.basedOn.name}&quot;</p>
          {item.changes?.length ? <ul>{item.changes.map((c) => <li key={c}>{c}</li>)}</ul> : <p className="ai-muted">No changes yet.</p>}
        </div>
      )}
      <div className={`ai-result-body widget report-widget ai-result-body--${item.displayType}`}>
        <div className="widget-body report-body">
          {table?.rows?.length
            ? <ReportDisplay table={table} config={{ ...(item.display || {}), displayType: item.displayType }} />
            : <div className="report-status">No rows</div>}
        </div>
      </div>
      <p className="ai-working"><span>How this was worked out</span>{item.described}</p>
      <div className="ai-card-actions">
        <button type="button" className="ai-button ai-button--ghost" onClick={() => onOpenInEditor(item)}>Open in editor</button>
        {item.draft && <span className="ai-muted">Not saved. Ask for changes, or ask to save it or add it to a dashboard.</span>}
      </div>
    </figure>
  );
}

/**
 * The assistant's questions. One question is answered with a click; several
 * are answered together: choose (or type) an answer to each, then send them.
 */
function QuestionCard({ item, disabled, onAnswer }) {
  const [chosen, setChosen] = useState({});
  const [typed, setTyped] = useState({});
  const many = item.questions.length > 1;
  const answerOf = (i) => (typed[i]?.trim() ? typed[i].trim() : chosen[i]);
  const complete = item.questions.every((_, i) => answerOf(i));

  if (item.answered) {
    return (
      <div className="ai-card ai-question ai-question--answered">
        {item.questions.map((q, i) => <p key={i} className="ai-question-text">{q.question}</p>)}
      </div>
    );
  }
  const sendAll = () => onAnswer(item.questions.map((q, i) => `${q.question} ${answerOf(i)}`).join('\n'));
  return (
    <div className="ai-card ai-question">
      {item.questions.map((q, i) => (
        <fieldset key={i} className="ai-question-block" disabled={disabled}>
          <legend className="ai-question-text">{many && <span className="ai-question-number">{i + 1}</span>}{q.question}</legend>
          <div className="ai-options">
            {q.options.map((o, j) => {
              const selected = many && chosen[i] === o.label && !typed[i]?.trim();
              return (
                <button
                  key={o.label}
                  type="button"
                  className={`ai-option ${selected ? 'ai-option--selected' : ''}`}
                  aria-pressed={many ? selected : undefined}
                  onClick={() => (many ? (setChosen((c) => ({ ...c, [i]: o.label })), setTyped((t) => ({ ...t, [i]: '' }))) : onAnswer(o.label))}
                >
                  <span className="ai-option-label">{o.label}{j === 0 && /recommend/i.test(o.description || '') ? <span className="ai-option-tag">Recommended</span> : null}</span>
                  {o.description && <span className="ai-option-description">{o.description}</span>}
                </button>
              );
            })}
          </div>
          {many && (
            <input
              type="text"
              className="ai-option-other"
              aria-label={`Your own answer to: ${q.question}`}
              placeholder="Or type your own answer"
              value={typed[i] || ''}
              onChange={(e) => setTyped((t) => ({ ...t, [i]: e.target.value }))}
            />
          )}
        </fieldset>
      ))}
      {many && (
        <div className="ai-card-actions">
          <button type="button" className="ai-button ai-button--primary" disabled={disabled || !complete} onClick={sendAll}>Send answers</button>
          {!complete && <span className="ai-muted">Answer each question to send.</span>}
        </div>
      )}
    </div>
  );
}

function ActionCard({ item, onConfirm, onCancel, onOpen }) {
  const [busy, setBusy] = useState(false);
  const act = (fn) => async () => {
    setBusy(true);
    try {
      await fn(item);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`ai-card ai-action ai-action--${item.status}`}>
      <p className="ai-action-label">{item.status === 'pending' ? 'Waiting for your confirmation' : 'Proposed change'}</p>
      <p className="ai-action-summary">{item.summary}</p>
      {item.status === 'pending' ? (
        <div className="ai-card-actions">
          <button type="button" className="ai-button ai-button--primary" disabled={busy} onClick={act(onConfirm)}>Confirm</button>
          <button type="button" className="ai-button ai-button--ghost" disabled={busy} onClick={act(onCancel)}>Cancel</button>
        </div>
      ) : (
        <p className="ai-action-status">
          {{ confirmed: 'Done.', cancelled: 'Cancelled.', failed: 'This could not be done.', expired: 'This has expired.' }[item.status] || item.status}
          {item.status === 'confirmed' && item.result?.link && (
            <> <button type="button" className="ai-link" onClick={() => onOpen(item.result.link)}>{item.result.dashboardName ? `Open ${item.result.dashboardName}` : `Open "${item.result.name}"`}</button></>
          )}
        </p>
      )}
    </div>
  );
}

/** Starting points: about the widget in focus, or general ones. */
function Suggestions({ focus, onSend, onPrefill }) {
  const items = focus
    ? [
      { label: 'Explain how this figure is worked out', send: `Explain how "${focus.title}" is worked out: which data, measures, filters and period it uses, and when the data last synced.` },
      { label: 'What stands out here?', send: `What stands out in "${focus.title}"? Point out anything unusual and say why.` },
      { label: 'Change this widget…', prefill: `Change "${focus.title}" so that ` },
    ]
    : [
      { label: 'What can you tell me about our data?', send: 'Which data do we have, and what kinds of questions can you answer from it?' },
      { label: 'Build me a report…', prefill: 'Build me a report showing ' },
      { label: 'Compare this month with last month…', prefill: 'Compare this month with last month for ' },
    ];
  return (
    <div className="ai-suggestions" role="group" aria-label="Suggestions">
      {items.map((s) => (
        <button key={s.label} type="button" className="ai-chip" onClick={() => (s.send ? onSend(s.send) : onPrefill(s.prefill))}>{s.label}</button>
      ))}
    </div>
  );
}

/** The AI assistant: docked on the right of every page, or collapsed to a tab. */
export default function AssistantPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const companyId = useStore((s) => s.currentCompany?.id);
  const dashboardId = useStore((s) => s.currentDashboardId);
  const wallMode = useStore((s) => s.wallMode);
  const { open, status, request, setStatus, openPanel, closePanel, takeRequest } = useAssistant();
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState([]);
  const [partial, setPartial] = useState('');
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [editorDraft, setEditorDraft] = useState(null);
  const [focus, setFocus] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const pendingContext = useRef(null);
  const abort = useRef(null);
  const partialRef = useRef('');
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const [choice, setChoiceState] = useState({});
  useEffect(() => { if (companyId) setChoiceState(readChoice(companyId)); }, [companyId]);
  const setChoice = (next) => { setChoiceState(next); saveChoice(companyId, next); };
  // Only what the company allows now; otherwise its default.
  const allowed = status?.models;
  const usedChoice = {
    model: allowed?.models?.some((m) => m.id === choice.model) ? choice.model : allowed?.defaultModel || null,
    reasoningEffort: allowed?.efforts?.some((e) => e.id === choice.reasoningEffort) ? choice.reasoningEffort : allowed?.defaultEffort || null,
  };

  // Whether the assistant is available here, checked when the company changes.
  useEffect(() => {
    if (!companyId) return;
    let live = true;
    assistantAPI.status(companyId)
      .then((res) => { if (live) setStatus(res.data); })
      .catch(() => { if (live) setStatus({ enabled: false, configured: false, available: false }); });
    return () => { live = false; };
  }, [companyId, setStatus]);

  const available = Boolean(status?.available);
  const docked = open && !wallMode && Boolean(status);
  // The page makes room for the docked panel instead of sitting under it.
  useEffect(() => {
    document.documentElement.classList.toggle('ai-docked', docked);
    return () => document.documentElement.classList.remove('ai-docked');
  }, [docked]);

  const loadList = useCallback(async () => {
    if (!companyId) return;
    try {
      setConversations((await assistantAPI.conversations(companyId)).data || []);
    } catch {
      setConversations([]);
    }
  }, [companyId]);

  const reset = () => {
    abort.current?.abort();
    setConversationId(null);
    setItems([]);
    setInput('');
    setError('');
  };

  // Opening the panel shows the latest conversations.
  useEffect(() => {
    if (!open) return;
    loadList();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, loadList]);

  // A request about a widget starts a new conversation focused on it, whether
  // the panel was collapsed or already open (a reply in progress is stopped).
  useEffect(() => {
    if (!open || !request) return;
    const taken = takeRequest();
    if (!taken) return;
    abort.current?.abort();
    reset();
    setConfirming(null);
    pendingContext.current = taken.context || null;
    setFocus(taken.focus || null);
    setInput(taken.prompt || '');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, request, takeRequest]);

  // A new company means new conversations (not on first load, which may carry a widget's focus).
  const shownCompany = useRef(companyId);
  useEffect(() => {
    if (shownCompany.current === companyId) return;
    shownCompany.current = companyId;
    setConversationId(null);
    setItems([]);
    setFocus(null);
  }, [companyId]);

  // Follow the conversation; when the assistant has just asked, show the start of its questions.
  useEffect(() => {
    const last = items[items.length - 1];
    const question = last?.type === 'question' && !last.answered ? document.querySelector('.ai-question:not(.ai-question--answered)') : null;
    if (question) question.scrollIntoView?.({ block: 'start' });
    else endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [items, partial, progress]);

  const load = async (id) => {
    if (!id) {
      reset();
      return;
    }
    try {
      const res = await assistantAPI.get(companyId, id);
      setConversationId(id);
      setItems(res.data.items || []);
      setFocus(null);
      setError('');
    } catch (err) {
      setError(describeError(err, 'Could not open the conversation'));
    }
  };

  const newConversation = () => {
    reset();
    setFocus(null);
    pendingContext.current = null;
    inputRef.current?.focus();
  };

  // Deleting one conversation (the one shown) or all of them, after a confirmation in the panel.
  const remove = async (which) => {
    try {
      if (which === 'all') await assistantAPI.removeAll(companyId);
      else await assistantAPI.remove(companyId, conversationId);
      setConfirming(null);
      newConversation();
      await loadList();
    } catch (err) {
      setError(describeError(err, 'Could not delete the conversation'));
    }
  };

  const clearFocus = () => {
    reset();
    setFocus(null);
    pendingContext.current = null;
  };

  const send = async (text) => {
    const message = String(text || '').trim();
    if (!message || busy) return;
    setError('');
    setBusy(true);
    setProgress([]);
    setPartial('');
    partialRef.current = '';
    setInput('');
    setItems((list) => [...list.map((i) => (i.type === 'question' ? { ...i, answered: true } : i)), { type: 'user', text: message }]);
    try {
      let id = conversationId;
      if (!id) {
        const context = pendingContext.current || pageContext(location, dashboardId);
        id = (await assistantAPI.start(companyId, context)).data.id;
        pendingContext.current = null;
        setConversationId(id);
      }
      abort.current = new AbortController();
      await sendMessage(companyId, id, message, (event, data) => {
        if (event === 'progress') setProgress((p) => [...p, data.label]);
        else if (event === 'text') {
          partialRef.current += data?.delta || '';
          setPartial((t) => t + (data?.delta || ''));
        } else if (event === 'message') {
          partialRef.current = '';
          setPartial('');
          setItems((list) => [...list, { type: 'assistant', text: data.text }]);
        } else if (event === 'result' || event === 'draft') {
          setProgress([]);
          setItems((list) => [...list, { type: 'result', ...data, draft: event === 'draft' }]);
        } else if (event === 'kpi') {
          setProgress([]);
          setItems((list) => [...list, { type: 'kpi', ...data }]);
        } else if (event === 'question') {
          setPartial('');
          setItems((list) => [...list, { type: 'question', callId: data.callId, questions: data.questions, answered: false }]);
        } else if (event === 'action') {
          setItems((list) => [...list, { type: 'action', actionId: data.actionId, summary: data.summary, status: 'pending' }]);
        } else if (event === 'error') {
          setError(data?.message || 'The AI assistant could not answer.');
        }
      }, abort.current.signal, usedChoice);
    } catch (err) {
      if (err.name === 'AbortError') {
        // Stopped by the user: keep what had arrived, marked as stopped.
        const said = partialRef.current.trim();
        setItems((list) => [...list, said ? { type: 'assistant', text: said, stopped: true } : { type: 'stopped' }]);
      } else {
        setError(describeError(err, 'The AI assistant could not answer.'));
      }
    } finally {
      setBusy(false);
      setProgress([]);
      setPartial('');
      loadList();
    }
  };

  const prefill = (text) => {
    setInput(text);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange?.(text.length, text.length);
    }, 0);
  };

  const setActionStatus = (actionId, change) => setItems((list) => list.map((i) => (i.type === 'action' && i.actionId === actionId ? { ...i, ...change } : i)));
  const confirm = async (item) => {
    try {
      const res = await assistantAPI.confirm(companyId, item.actionId);
      setActionStatus(item.actionId, { status: 'confirmed', result: res.data?.result || null });
    } catch (err) {
      setActionStatus(item.actionId, { status: 'failed' });
      setError(describeError(err, 'The change could not be made.'));
    }
  };
  const cancel = async (item) => {
    try {
      await assistantAPI.cancel(companyId, item.actionId);
      setActionStatus(item.actionId, { status: 'cancelled' });
    } catch (err) {
      setError(describeError(err, 'Could not cancel.'));
    }
  };

  const openLink = (link) => {
    // A dashboard link opens the dashboard the change was made to.
    const target = items.find((i) => i.type === 'action' && i.result?.link === link)?.result;
    if (target?.dashboardId) {
      const { dashboards, setCurrentDashboard } = useStore.getState();
      const dashboard = (dashboards || []).find((d) => Number(d.id) === Number(target.dashboardId));
      if (dashboard) setCurrentDashboard(dashboard);
      else localStorage.setItem('currentDashboardId', target.dashboardId);
    }
    navigate(link);
  };

  const openInEditor = (item) => setEditorDraft({
    name: item.title,
    description: item.described || '',
    cubeQuery: item.cubeQuery,
    display: { ...(item.display || {}), displayType: item.displayType },
  });
  const saveFromEditor = async (report) => {
    const res = await savedReportsAPI.create(companyId, report);
    if (!res?.success) throw new Error(res?.error || 'Failed to save report');
    setEditorDraft(null);
    navigate(`/reports/${res.data.id}`);
  };

  const unavailable = useMemo(() => {
    if (!status) return null;
    if (!status.configured) return 'The AI assistant is not set up on this server yet.';
    if (!status.enabled) return status.canManage
      ? 'The AI assistant is off for this company. You can turn it on in Settings.'
      : 'The AI assistant is off for this company. A company admin can turn it on in Settings.';
    return null;
  }, [status]);

  const editor = editorDraft && <ReportEditor initialReport={editorDraft} onClose={() => setEditorDraft(null)} onCreate={saveFromEditor} onUpdate={() => {}} />;

  if (wallMode) return editor || null;
  if (!open) {
    return (
      <>
        {available && (
          <button type="button" className="ai-tab" onClick={() => openPanel()} aria-label="Open the AI assistant">
            <AiMark size={18} />
            <span>AI assistant</span>
          </button>
        )}
        {editor}
      </>
    );
  }

  const lastQuestion = [...items].reverse().find((i) => i.type === 'question');
  // Only the latest draft is shown in full; each new draft replaces the one before.
  const isDraft = (i) => i.type === 'draft' || (i.type === 'result' && i.draft);
  const latestDraft = [...items].reverse().find(isDraft);
  const latestKpi = [...items].reverse().find((i) => i.type === 'kpi');
  const lastProgress = progress[progress.length - 1];

  return (
    <>
      <aside className="ai-panel" aria-label="AI assistant">
        <header className="ai-header">
          <div className="ai-title">
            <span className="ai-title-mark"><AiMark size={20} /></span>
            <div>
              <h2>AI assistant</h2>
              <p>Answers from your company&apos;s data</p>
            </div>
          </div>
          <div className="ai-header-tools">
            <button type="button" className="ai-icon-button" onClick={newConversation} disabled={busy} aria-label="New conversation" title="New conversation">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            </button>
            <button type="button" className="ai-icon-button" onClick={closePanel} aria-label="Collapse the AI assistant" title="Collapse">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M13 6l6 6-6 6M5 6l6 6-6 6" /></svg>
            </button>
          </div>
        </header>
        {conversations.length > 0 && (
          <div className="ai-history">
            <div className="ai-history-row">
              <select aria-label="Earlier conversations" value={conversationId || ''} disabled={busy} onChange={(e) => { setConfirming(null); load(e.target.value ? Number(e.target.value) : null); }}>
                <option value="">Earlier conversations</option>
                {conversations.map((c) => <option key={c.id} value={c.id}>{c.title || `Conversation ${c.id}`}</option>)}
              </select>
              <button type="button" className="ai-icon-button" disabled={busy || !conversationId} onClick={() => setConfirming('one')} aria-label="Delete this conversation" title="Delete this conversation">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg>
              </button>
              <button type="button" className="ai-link ai-history-clear" disabled={busy} onClick={() => setConfirming('all')}>Delete all</button>
            </div>
            {confirming && (
              <div className="ai-confirm" role="alertdialog" aria-label="Confirm deletion">
                <span>
                  {confirming === 'all'
                    ? `Delete all ${conversations.length} of your conversations? This cannot be undone.`
                    : 'Delete this conversation? This cannot be undone.'}
                </span>
                <button type="button" className="ai-button ai-button--danger" onClick={() => remove(confirming)}>Delete</button>
                <button type="button" className="ai-button ai-button--ghost" onClick={() => setConfirming(null)}>Cancel</button>
              </div>
            )}
          </div>
        )}
        {focus && (
          <div className="ai-focus">
            <span className="ai-focus-label">About</span>
            <span className="ai-focus-title" title={focus.title}>{focus.title}</span>
            <button type="button" className="ai-focus-clear" onClick={clearFocus} aria-label={`Stop focusing on ${focus.title}`} title="Ask about anything">&times;</button>
          </div>
        )}

        <div className="ai-body" aria-live="polite">
          {unavailable && <p className="ai-unavailable">{unavailable}</p>}
          {!unavailable && items.length === 0 && !busy && (
            <div className="ai-empty">
              <div className="ai-empty-mark"><AiMark size={34} /></div>
              <h3>{focus ? `Ask about "${focus.title}"` : 'Ask about your data'}</h3>
              <p>{focus
                ? 'Ask how it is worked out, what the figures mean, or for a change such as another column or period.'
                : 'Ask a question, or ask for a report or chart. Every figure comes from your data, with how it was worked out, and nothing is saved without your confirmation.'}</p>
              <Suggestions focus={focus} onSend={send} onPrefill={prefill} />
            </div>
          )}
          {items.map((item, i) => {
            if (item.type === 'user') return <div key={i} className="ai-msg ai-msg--user">{item.text}</div>;
            if (item.type === 'assistant') return <div key={i} className="ai-msg ai-msg--assistant"><AssistantText text={item.text} />{item.stopped && <span className="ai-stopped-tag">Stopped</span>}</div>;
            if (item.type === 'stopped') return <p key={i} className="ai-superseded">You stopped this reply.</p>;
            if (isDraft(item) && item !== latestDraft) return <p key={i} className="ai-superseded">Earlier draft: {item.title} (replaced below)</p>;
            if (item.type === 'result' || item.type === 'draft') return <ResultCard key={i} item={item.type === 'draft' ? { ...item, draft: true } : item} onOpenInEditor={openInEditor} />;
            if (item.type === 'kpi') return item === latestKpi ? <KpiCard key={i} item={item} /> : <p key={i} className="ai-superseded">Earlier KPI draft: {item.title} (replaced below)</p>;
            if (item.type === 'question') return <QuestionCard key={item.callId || i} item={item} disabled={busy || item !== lastQuestion} onAnswer={send} />;
            if (item.type === 'action') return <ActionCard key={i} item={item} onConfirm={confirm} onCancel={cancel} onOpen={openLink} />;
            return null;
          })}
          {busy && (
            partial
              ? <div className="ai-msg ai-msg--assistant ai-msg--streaming"><AssistantText text={partial} /></div>
              : (
                <div className="ai-thinking" role="status">
                  <span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>
                  <span>{lastProgress ? `${lastProgress}…` : 'Thinking…'}</span>
                </div>
              )
          )}
          {error && <div className="ai-error" role="alert">{error}</div>}
          <div ref={endRef} />
        </div>

        <form className="ai-compose" onSubmit={(e) => { e.preventDefault(); send(input); }}>
          <div className="ai-compose-box">
            <textarea
              ref={inputRef}
              aria-label="Message to the AI assistant"
              placeholder={lastQuestion && !lastQuestion.answered ? 'Answer above, or type here' : focus ? `Ask about "${focus.title}"` : 'Ask about your data'}
              value={input}
              rows={2}
              disabled={Boolean(unavailable)}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            />
            {busy ? (
              <button type="button" className="ai-send ai-stop" onClick={() => abort.current?.abort()} aria-label="Stop the reply" title="Stop">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><rect x="5" y="5" width="14" height="14" rx="2.5" fill="currentColor" /></svg>
              </button>
            ) : (
              <button type="submit" className="ai-send" disabled={!input.trim() || Boolean(unavailable)} aria-label="Send">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </button>
            )}
          </div>
          <div className="ai-compose-foot">
            <ModelChoice settings={allowed} value={usedChoice} onChange={setChoice} disabled={busy} />
            <p className="ai-compose-hint">{busy ? 'Working… press Stop to end the reply' : 'Enter to send, Shift+Enter for a new line'}</p>
          </div>
        </form>
      </aside>
      {editor}
    </>
  );
}
